import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/ui/Layout";
import ProgressBar from "../components/ui/ProgressBar";
import ScrollToTopButton from "../components/ui/ScrollToTopButton";
import {
  IsoBeforeChaos,
  IsoSystemStackImage,
  IsoContributionLoop,
  IsoCampaignProof,
} from "../components/collabstr/CollabstrIsometric";
import "./Collabstr.css";
import { COLLABSTR_DESIGN_SYSTEM_COVER } from "../data/caseStudyMedia";
import CaseOpen from "../components/ui/CaseOpen";

const HEADLINE =
  "Campaign creation, the flow at the heart of our marketplace, went from 56 minutes and 20+ steps to 9 minutes and 2 clicks.";

const LAYERS = [
  {
    name: "Tokens",
    detail: "Color, type, and spacing: the smallest shared decisions so screens don’t re-debate basics.",
  },
  {
    name: "Components",
    detail: "Buttons, inputs, and core UI: only what product work actually needed in v1.",
  },
  {
    name: "Patterns",
    detail: "Repeated layouts (lists, cards, steppers) so new flows compose instead of reinvent.",
  },
  {
    name: "SOPs",
    detail: "Propose → Review → Merge → Announce: one owner, no silent drift.",
  },
  {
    name: "design.md",
    detail: "Vision and usage in writing, not only in Figma, so eng and growth share one source.",
  },
];

const SECTIONS = [
  {
    id: "start",
    title: "Where we started",
    Illustration: IsoBeforeChaos,
    caption: "One-off screens: quality depended on who built it.",
    body: (
      <p>
        When I joined as founding product designer, every screen was a one-off. No shared components, no common language
        with engineering, and every feature started from a blank page. As the team grew, that gap was only going to widen.
      </p>
    ),
  },
  {
    id: "condo",
    title: "The bet: ship a condo, not a house",
    Illustration: null,
    caption: null,
    body: (
      <p>
        A traditional design system takes a quarter. We didn&apos;t have one; we had a product to ship. I built the
        smallest stack that could hold weight: tokens, core components, and patterns, shipped in one week with an
        AI-assisted workflow, then grown only where real work demanded it. No speculative library. No
        design-system-for-its-own-sake.
      </p>
    ),
  },
  {
    id: "stack",
    title: "What’s in the stack",
    Illustration: IsoSystemStackImage,
    caption: "Bottom-up layers: each level supports the one above.",
    body: (
      <ul className="collabstr-layer-list">
        {LAYERS.map((layer) => (
          <li key={layer.name}>
            <strong>{layer.name}</strong>: {layer.detail}
          </li>
        ))}
      </ul>
    ),
  },
  {
    id: "contribution",
    title: "A system that maintains itself",
    Illustration: IsoContributionLoop,
    caption: "Contribution model from day one, drift-proof by design.",
    body: (
      <>
        <p>
          Most systems die by drift, not launch. Anyone can propose a component or variant. I review every merge as
          single owner; every merge is announced so the library stays visible. Rules live in documented SOPs, not in my
          head.
        </p>
        <p className="collabstr-loop-label">Propose → Review → Merge → Announce</p>
      </>
    ),
  },
  {
    id: "onboarding",
    title: "Onboarding by building",
    Illustration: null,
    caption: null,
    body: (
      <p>
        New designers don&apos;t learn by reading alone. Their first assignment is a real contribution: variants the
        product needs. They learn by extending the stack; the stack gets stronger with every hire. Onboarding is product
        work, not a side quest.
      </p>
    ),
  },
  {
    id: "proof",
    title: "The proof: campaign creation",
    Illustration: IsoCampaignProof,
    caption: HEADLINE,
    body: (
      <p>
        As foundations matured, we rebuilt our most important flow on top of them. What had been 20+ steps across pages
        became two clicks on one page. That is what the system is for: not consistency theater, but a core business flow
        made six times faster.
      </p>
    ),
  },
  {
    id: "beyond",
    title: "The same system beyond design",
    Illustration: null,
    caption: null,
    body: (
      <>
        <p>
          Campaign creation became a standard operating path for the whole product org. Engineering ships against the
          same components; growth references the same journey instead of one-off screenshots. We improve one path instead
          of branching special cases.
        </p>
        <p>
          Shared language for how work ships: small-team speed that usually needs a much larger org.
        </p>
      </>
    ),
  },
];

const RESULTS = [
  <>
    <strong className="font-semibold text-gray-900">Campaign creation: 56 min → 9 min</strong>, 20+ steps → 2 clicks
  </>,
  <>Every new designer ships a real system contribution as part of onboarding</>,
  <>One contribution pipeline, one owner: no forked components, no design drift</>,
];

const Collabstr = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <a
        href="#collabstr-system"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:bg-white focus:p-4 focus:z-50 focus:text-black focus:underline"
      >
        Skip to Content
      </a>

      <ProgressBar />
      <ScrollToTopButton />

      <article className="collabstr-case">
        <CaseOpen
          src={COLLABSTR_DESIGN_SYSTEM_COVER}
          alt="Collabstr design system, condo not a house"
          eyebrow="Case study · Collabstr · Design system"
          title="Building the system that let a small team move like a big one, built to replicate and iterate"
          dek="How I shipped a design system in one week as founding designer, and made it grow every time someone joined."
          overviewId="collabstr-overview"
          meta={[
            { label: "Role", value: "Founding Product Designer" },
            { label: "Timeline", value: "Dec 2025 to Present" },
            { label: "Scope", value: "Design system · onboarding · SOPs" },
          ]}
        />

        <div id="collabstr-system" className="collabstr-case__body container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 max-w-3xl">
          {SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="collabstr-section">
              <h2 className="collabstr-section__title">{section.title}</h2>
              {section.Illustration ? (
                <figure className="collabstr-figure collabstr-figure--iso">
                  <section.Illustration />
                  {section.caption ? <figcaption>{section.caption}</figcaption> : null}
                </figure>
              ) : null}
              <div className="collabstr-section__prose">{section.body}</div>
            </section>
          ))}

          <section className="collabstr-section">
            <h2 className="collabstr-section__title">Results</h2>
            <ul className="collabstr-results">
              {RESULTS.map((result, i) => (
                <li key={i}>{result}</li>
              ))}
            </ul>
          </section>

          <section className="collabstr-section">
            <h2 className="collabstr-section__title">What the condo cost us</h2>
            <div className="collabstr-section__prose">
              <p>
                A condo isn&apos;t free. Some early components were built for one use case and reworked when a second
                arrived. My rule since:{" "}
                <strong>a component earns its place the second time it&apos;s needed, not the first.</strong> Small
                rebuild cost occasionally beats a cathedral nobody moves into.
              </p>
            </div>
          </section>
        </div>
      </article>

      <div className="container mx-auto px-6 sm:px-8 md:px-12 lg:px-16 max-w-7xl py-12 mt-16 border-t border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
          <Link
            to="/#my-works"
            className="flex items-center gap-2 text-lg text-primary hover:opacity-70 transition-opacity font-light"
          >
            ← Back to Portfolio
          </Link>
          <Link
            to="/intranetconnections"
            className="flex items-center gap-2 text-lg text-primary hover:opacity-70 transition-opacity font-light"
          >
            Next Case Study: Intranet Connections →
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default Collabstr;
